/**
 * Moves one template to a new lifecycle status (RND-76) from OUTSIDE the editor — the row menu's
 * Archive / Unarchive.
 *
 * `PATCH` is a full replacement (contract §4): `status` is the only optional key, and everything
 * else is `required`. The index row carries no `data`, so a status-only body is a 422 — the
 * template is fetched first and sent back whole with the status swapped. `show` normalises the
 * document (B7) exactly as the editor does before its own save, so the round trip changes nothing
 * but `status`.
 *
 * Publish is deliberately NOT reachable through here: it needs the document validated clean, and
 * the editor page hook is where that gate lives.
 */
import { ref } from 'vue'

import type { Ref } from 'vue'
import type { AssessmentTemplate } from './interfaces'
import type { AssessmentTemplateStatus } from './enums'

import { useAssessmentTemplateShowApi } from '../../api/assessment-templates/show.hook'
import { useAssessmentTemplateUpdateApi } from '../../api/assessment-templates/update.hook'

export interface AssessmentTemplateStatusChangeOptions {
  templateId: number | string
  status: AssessmentTemplateStatus
}

export interface UseAssessmentTemplateStatusChangeReturn {
  /** A change is in flight. One at a time per hook instance — a second call while busy is a no-op. */
  refIsChanging: Ref<boolean>
  /** The updated template, or `null` on any failure (the API hooks toast their own reasons). */
  changeStatus: (options: AssessmentTemplateStatusChangeOptions) => Promise<AssessmentTemplate | null>
}

export const useAssessmentTemplateStatusChange = (): UseAssessmentTemplateStatusChangeReturn => {
  const refIsChanging = ref<boolean>(false)

  const changeStatus = async (
    { templateId, status }: AssessmentTemplateStatusChangeOptions,
  ): Promise<AssessmentTemplate | null> => {
    if (refIsChanging.value) return null

    refIsChanging.value = true

    try {
      const template = await useAssessmentTemplateShowApi({ templateId })
      if (!template) return null

      // Already there — a stale row whose status moved under us. Nothing to send.
      if (template.status === status) return template

      return await useAssessmentTemplateUpdateApi({
        templateId,
        payload: {
          title: template.title,
          type: template.type,
          discipline_code: template.discipline_code,
          grade: template.grade,
          status,
          data: template.data,
        },
      })
    } finally {
      refIsChanging.value = false
    }
  }

  return {
    refIsChanging,
    changeStatus,
  }
}
